import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";

const FALLBACK_IMG = "https://images.pexels.com/photos/1192031/pexels-photo-1192031.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function RecipeDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [r, setR] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareToken, setShareToken] = useState(null);
  const [sharing, setSharing] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/recipes/${id}`);
        setR(data);
        if (data.share_token) setShareToken(data.share_token);
      } catch {
        toast.error("Recipe not found");
        nav("/app");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, nav]);

  const buildList = async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/shopping-lists", { recipe_ids: [id], name: r.title });
      toast.success("List created");
      nav(`/app/shopping/${data.list_id}`);
    } catch (e) {
      toast.error("Could not create list");
    } finally {
      setCreating(false);
    }
  };

  const openShare = async () => {
    setShareOpen(true);
    if (shareToken) return;
    setSharing(true);
    try {
      const { data } = await api.post(`/recipes/${id}/share`);
      setShareToken(data.share_token);
    } catch {
      toast.error("Could not create share link");
      setShareOpen(false);
    } finally {
      setSharing(false);
    }
  };

  const revokeShare = async () => {
    if (!confirm("Stop sharing this recipe? The link will stop working.")) return;
    try {
      await api.delete(`/recipes/${id}/share`);
      setShareToken(null);
      setShareOpen(false);
      toast.success("Share link revoked");
    } catch {
      toast.error("Could not revoke");
    }
  };

  const shareUrl = shareToken ? `${window.location.origin}/share/${shareToken}` : "";

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link copied");
    } catch {
      toast.error("Copy failed — select & copy manually");
    }
  };

  if (loading) return <div className="text-[#5C6B61]">Loading…</div>;
  if (!r) return null;

  return (
    <div className="space-y-8 ff-fade-in">
      <Link to="/app/recipes" className="text-sm text-[#5C6B61] hover:text-[#3B5A3E] inline-flex items-center gap-1">
        ← All recipes
      </Link>

      <div className="grid lg:grid-cols-[1.2fr_1fr] gap-8">
        <div className="ff-card overflow-hidden">
          <img
            src={r.image || FALLBACK_IMG}
            alt={r.title}
            onError={(e) => { e.target.src = FALLBACK_IMG; }}
            className="w-full aspect-[4/3] object-cover"
          />
        </div>
        <div>
          <div className="ff-label">Recipe</div>
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight mt-2" style={{ fontFamily: "Outfit, sans-serif" }}>
            {r.title}
          </h1>
          {r.source_url && (
            <a href={r.source_url} target="_blank" rel="noreferrer"
               className="text-xs text-[#3B5A3E] hover:underline mt-2 inline-block">
              source ↗
            </a>
          )}
          <div className="flex flex-wrap gap-2 mt-4">
            {(r.tags || []).map((t) => <span key={t} className="ff-chip">{t}</span>)}
          </div>

          <div className="grid grid-cols-4 gap-3 mt-6">
            <Stat label="Serves" value={r.servings} />
            <Stat label="Calories" value={r.calories_per_serving ?? "—"} accent />
            <Stat label="Protein" value={r.protein_g != null ? `${r.protein_g}g` : "—"} />
            <Stat label="Carbs" value={r.carbs_g != null ? `${r.carbs_g}g` : "—"} />
          </div>

          <div className="flex flex-wrap gap-3 mt-6">
            <button
              onClick={buildList}
              disabled={creating}
              data-testid="recipe-build-list-btn"
              className="ff-btn-primary disabled:opacity-60"
            >
              {creating ? "Building…" : "Send to shopping list"}
            </button>
            <button
              onClick={openShare}
              data-testid="share-recipe-btn"
              className="ff-btn-ghost border border-[#E5E2DA]"
            >
              {shareToken ? "🔗 Manage share" : "🔗 Share recipe"}
            </button>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr_1.4fr] gap-8">
        <section className="ff-card p-6">
          <div className="ff-label mb-4">Ingredients</div>
          <ul className="space-y-3">
            {r.ingredients.map((i, idx) => (
              <li key={idx} data-testid="ingredient-row" className="flex items-start gap-3 py-2 border-b border-[#E5E2DA] last:border-0">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#3B5A3E]" />
                <div>
                  <div className="text-sm font-medium">{i.name}</div>
                  {(i.quantity || i.notes) && (
                    <div className="text-xs text-[#8B9690] mt-0.5">
                      {[i.quantity, i.notes].filter(Boolean).join(" · ")}
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </section>

        <section className="ff-card p-6">
          <div className="ff-label mb-4">Method</div>
          {r.steps?.length ? (
            <ol className="space-y-4">
              {r.steps.map((s, idx) => (
                <li key={idx} className="flex gap-4">
                  <span className="flex-none w-8 h-8 rounded-full bg-[#F3F0EA] text-[#3B5A3E] text-sm font-medium flex items-center justify-center">
                    {idx + 1}
                  </span>
                  <p className="text-[#2A332C] leading-relaxed text-[15px]">{s}</p>
                </li>
              ))}
            </ol>
          ) : (
            <p className="text-[#8B9690] text-sm">No steps captured — check the source.</p>
          )}
        </section>
      </div>

      {/* Share modal */}
      {shareOpen && (
        <div
          className="fixed inset-0 z-40 bg-[#2A332C]/40 backdrop-blur-sm flex items-end md:items-center justify-center p-4"
          onClick={() => setShareOpen(false)}
          data-testid="share-modal"
        >
          <div
            className="ff-card w-full max-w-lg p-6 md:p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="ff-label">Share recipe</div>
            <h2 className="text-2xl font-semibold mt-1" style={{ fontFamily: "Outfit, sans-serif" }}>
              Anyone with this link can view {r.title}
            </h2>
            <p className="text-sm text-[#5C6B61] mt-1">
              No account needed. Calories, ingredients and method only — your other recipes stay private.
            </p>

            {sharing && <div className="mt-6 text-sm text-[#5C6B61]">Generating link…</div>}

            {shareToken && (
              <div className="mt-5 space-y-3">
                <div className="flex items-center gap-2 p-3 bg-[#F3F0EA] rounded-xl">
                  <input
                    readOnly
                    value={shareUrl}
                    data-testid="share-link-input"
                    className="flex-1 bg-transparent outline-none text-sm font-mono text-[#2A332C] min-w-0"
                    onFocus={(e) => e.target.select()}
                  />
                  <button onClick={copyLink} data-testid="copy-link-btn" className="text-xs font-medium bg-[#3B5A3E] text-white px-3 py-1.5 rounded-full hover:bg-[#2A402D] flex-none">
                    Copy
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  <a
                    href={`https://wa.me/?text=${encodeURIComponent(`Check out ${r.title} on Shop, Cal & Meal: ${shareUrl}`)}`}
                    target="_blank" rel="noreferrer"
                    className="text-xs font-medium px-3 py-2 rounded-full bg-[#25D366] text-white"
                  >WhatsApp</a>
                  <a
                    href={`mailto:?subject=${encodeURIComponent(r.title)}&body=${encodeURIComponent(`I made this on Shop, Cal & Meal — give it a try:\n\n${shareUrl}`)}`}
                    className="text-xs font-medium px-3 py-2 rounded-full bg-[#F3F0EA] text-[#3B5A3E]"
                  >Email</a>
                  <a
                    href={shareUrl}
                    target="_blank" rel="noreferrer"
                    className="text-xs font-medium px-3 py-2 rounded-full bg-[#F3F0EA] text-[#3B5A3E]"
                  >Preview ↗</a>
                </div>
              </div>
            )}

            <div className="mt-6 flex gap-3 justify-end">
              {shareToken && (
                <button onClick={revokeShare} data-testid="revoke-share-btn" className="text-sm text-[#C36952] hover:underline">
                  Stop sharing
                </button>
              )}
              <button onClick={() => setShareOpen(false)} className="ff-btn-ghost">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className={`rounded-2xl p-3 text-center ${accent ? "bg-[#FBEFEA]" : "bg-[#F3F0EA]"}`}>
      <div className={`text-[10px] uppercase tracking-[0.2em] font-semibold ${accent ? "text-[#A65541]" : "text-[#5C6B61]"}`}>{label}</div>
      <div className={`text-lg font-semibold mt-1 ${accent ? "text-[#A65541]" : "text-[#2A332C]"}`} style={{ fontFamily: "Outfit, sans-serif" }}>{value}</div>
    </div>
  );
}
